/**
 * <s-carousel> — a horizontally-scrollable "swim lane" that shows multiple items at once.
 *
 * UI-only: it wires the optional prev/next buttons to scroll the track by roughly one page
 * and toggles their `disabled` state at the start/end of the scroll range. The track is a
 * native scroll-snap container, so scrolling, touch/trackpad swiping, and keyboard focus
 * all work without JS — this component only enhances the arrow controls.
 *
 * Expected DOM (rendered by blocks/carousel.liquid):
 *   <s-carousel>
 *     [<button data-carousel-prev> / <button data-carousel-next>]
 *     <div data-carousel-track> …items… </div>
 *   </s-carousel>
 */
class SCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    this.prevButton = this.querySelector("[data-carousel-prev]");
    this.nextButton = this.querySelector("[data-carousel-next]");

    if (!this.track) return;

    this.onPrev = () => this.scrollByPage(-1);
    this.onNext = () => this.scrollByPage(1);
    this.onScroll = () => this.updateControls();

    this.prevButton?.addEventListener("click", this.onPrev);
    this.nextButton?.addEventListener("click", this.onNext);
    this.track.addEventListener("scroll", this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);

    this.updateControls();
    this.setupAutoplay();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener("click", this.onPrev);
    this.nextButton?.removeEventListener("click", this.onNext);
    this.track?.removeEventListener("scroll", this.onScroll);
    this.resizeObserver?.disconnect();
    this.teardownAutoplay?.();
  }

  /**
   * Optional autoplay (`autoplay` + `autoplay-interval` attributes): advances one item at a
   * time and loops back to the start. Keeps moving on hover; holds during keyboard focus,
   * touch, or while off-screen. Reduced-motion visitors get instant steps instead of glides.
   */
  setupAutoplay() {
    if (!this.hasAttribute("autoplay")) return;

    const interval = Math.max(1000, Number(this.getAttribute("autoplay-interval")) || 4000);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const behavior = () => (reducedMotion.matches ? "auto" : "smooth");
    const holds = new Set();
    const hold = (reason) => () => holds.add(reason);
    const release = (reason) => () => holds.delete(reason);

    // Touch pauses for a short window instead of a held flag: on phones a touch often turns into
    // a page scroll and ends with touchcancel (or nothing), which would otherwise freeze autoplay.
    let touchPausedUntil = 0;
    const pauseForTouch = () => { touchPausedUntil = Date.now() + interval * 2; };

    const tick = () => {
      if (holds.size > 0 || document.hidden || Date.now() < touchPausedUntil) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      if (maxScroll <= 1) return;
      if (this.track.scrollLeft >= maxScroll - 1) {
        this.track.scrollTo({ left: 0, behavior: behavior() });
        return;
      }
      const first = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = first ? first.getBoundingClientRect().width + gap : this.track.clientWidth;
      this.track.scrollBy({ left: step, behavior: behavior() });
    };

    const listeners = [
      ["focusin", (event) => { if (event.target.matches(":focus-visible")) holds.add("focus"); }],
      ["focusout", release("focus")],
      ["touchstart", pauseForTouch],
      ["touchend", pauseForTouch],
    ];
    listeners.forEach(([type, fn]) => this.addEventListener(type, fn, { passive: true }));

    this.visibilityObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) holds.delete("offscreen");
      else holds.add("offscreen");
    });
    this.visibilityObserver.observe(this);

    this.autoplayTimer = setInterval(tick, interval);

    this.teardownAutoplay = () => {
      clearInterval(this.autoplayTimer);
      this.visibilityObserver?.disconnect();
      listeners.forEach(([type, fn]) => this.removeEventListener(type, fn));
    };
  }

  /**
   * Scroll the track by ~80% of its visible width in the given direction.
   * @param {number} direction -1 for previous, 1 for next.
   */
  scrollByPage(direction) {
    const amount = this.track.clientWidth * 0.8 * direction;
    this.track.scrollBy({ left: amount, behavior: "smooth" });
  }

  /** Disable prev/next when the track is at its respective end. */
  updateControls() {
    if (!this.prevButton && !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= maxScroll - 1;
    const overflowing = maxScroll > 1;

    if (this.prevButton) this.prevButton.disabled = !overflowing || atStart;
    if (this.nextButton) this.nextButton.disabled = !overflowing || atEnd;
  }
}

if (!customElements.get("s-carousel")) {
  customElements.define("s-carousel", SCarousel);
}
